import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import AppButton from '../../components/AppButton/AppButton';
import AppInput from '../../components/AppInput/AppInput';
import { useSelector, useDispatch } from 'react-redux';
import { Package, FileText, DollarSign, Calendar, Tag } from 'lucide-react';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import { PackageValidationSchema } from '../../validationscheema/PackageValidation';
import { HitApi } from '../../Api/ApiHit';
import { addPackage, searchPackage, updatePackage, searchUnit, searchSalonChild } from '../../constant/Constant';
import SearchableSelect from '../../components/AppSelect/SearchableSelect';
import MultiSelect from '../../components/AppSelect/MultiSelect';
import toast from 'react-hot-toast';
import { getErrorMsg, hasError } from '../../utils/utils';
import { getSelectedUnit } from '../../storage/Storage';
import TextArea from '../../components/AppInput/TextArea';
import { duration, statusOptions } from '../../constant/Options';
import AppSwitch from '../../components/Switch/AppSwitch';

function AddPackage() {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [servicesOptions, setServicesOptions] = useState([]);
    const [unitsOptions, setUnitsOptions] = useState([]);

    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const url = window.location.pathname;
    const urlId = url.split('/')[3];

    const loadOptionsAtOnce = async () => {
        setIsLoadingOptions(true);
        try {
            // Load services (Child services)
            const servicesJson = {
                page: 1,
                limit: 1000,
                search: {}
            };
            const servicesRes = await HitApi(servicesJson, searchSalonChild);
            if (servicesRes?.statusCode === 200) {
                const servicesData = servicesRes?.data?.docs || [];
                const formattedServices = servicesData.map(service => ({
                    value: service._id,
                    label: service.name,
                    price: service.price || 0
                }));
                setServicesOptions(formattedServices);
            }

            // Load units
            const unitsJson = {
                page: 1,
                limit: 1000,
                search: {}
            };
            const unitsRes = await HitApi(unitsJson, searchUnit);
            if (unitsRes?.statusCode === 200) {
                const unitsData = unitsRes?.data?.docs || [];
                const formattedUnits = unitsData.map(unit => ({
                    value: unit._id,
                    label: unit.unitName
                }));
                setUnitsOptions(formattedUnits);
            }
        } catch (error) {
            toast.error('Error loading options. Please refresh the page.');
        } finally {
            setIsLoadingOptions(false);
        }
    };

    useEffect(() => {
        // Load options first
        loadOptionsAtOnce();

        if (urlId) {
            setIsEditMode(true);
            loadData();
        } else {
            // Initialize with empty data for add mode
            const initialPackageData = {
                name: "",
                description: "",
                services: [],
                totalActualPrice: 0,
                packagePrice: 0,
                discountAmount: 0,
                discountPercentage: 0,
                validityValue: 30,
                validityUnit: "days",
                isActive: false,
                unitIds: getSelectedUnit()?._id
            };
            dispatch(setApiJson(initialPackageData));
            dispatch(setApiErrorJson({}));
        }
    }, [dispatch, urlId]);

    // Auto-calculate Total Actual Price when services change
    useEffect(() => {
        if (ApiReducer?.apiJson?.services && servicesOptions.length > 0) {
            const selectedServiceIds = ApiReducer.apiJson.services;

            // Calculate total price from selected services
            const totalPrice = selectedServiceIds.reduce((sum, serviceId) => {
                const service = servicesOptions.find(s => s.value === serviceId);
                return sum + (service?.price ? parseFloat(service.price) : 0);
            }, 0);

            // Only update if the calculated price is different from current value
            if (totalPrice !== parseFloat(ApiReducer.apiJson.totalActualPrice || 0)) {
                dispatch(setApiJson({
                    ...ApiReducer.apiJson,
                    totalActualPrice: totalPrice
                }));
            }
        }
    }, [ApiReducer?.apiJson?.services, servicesOptions]);

    const loadData = async () => {
        try {
            setLoading(true);
            const json = {
                page: 1,
                limit: 10,
                search: {
                    _id: urlId
                }
            };

            const res = await HitApi(json, searchPackage);

            if (res?.statusCode === 200) {
                const packageData = res?.data?.docs?.[0];

                if (packageData) {
                    // Convert services array from objects to IDs
                    let serviceIds = [];
                    if (packageData.services && Array.isArray(packageData.services)) {
                        serviceIds = packageData.services.map(service => {
                            // If service is an object with _id, extract the _id
                            if (typeof service === 'object' && service._id) {
                                return service._id;
                            }
                            // If service is already an ID string, return it
                            return service;
                        });
                    }

                    // Convert unitIds array from objects to ID
                    let unitId = getSelectedUnit()?._id;
                    if (packageData.unitIds && Array.isArray(packageData.unitIds) && packageData.unitIds.length > 0) {
                        // If unitIds is an array of objects with _id, extract the first _id
                        if (typeof packageData.unitIds[0] === 'object' && packageData.unitIds[0]._id) {
                            unitId = packageData.unitIds[0]._id;
                        }
                        // If unitIds is already an array of ID strings, get the first one
                        else if (typeof packageData.unitIds[0] === 'string') {
                            unitId = packageData.unitIds[0];
                        }
                    }

                    // Flatten validity object to separate fields
                    const validityValue = packageData.validity?.value || 30;
                    const validityUnit = packageData.validity?.unit || "days";

                    // Ensure data is properly formatted
                    const formattedData = {
                        ...packageData,
                        services: serviceIds,
                        unitIds: unitId,
                        validityValue: validityValue,
                        validityUnit: validityUnit
                    };

                    dispatch(setApiJson(formattedData));
                    dispatch(setApiErrorJson({}));
                } else {
                    toast.error('Package not found');
                    navigate('/packages');
                }
            } else {
                toast.error('Failed to load package data');
            }
        } catch (error) {
            console.error('Error loading package data:', error);
            toast.error('Error loading package data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calculateDiscountAmount = () => {
        const totalActualPrice = parseFloat(ApiReducer?.apiJson?.totalActualPrice || 0);
        const packagePrice = parseFloat(ApiReducer?.apiJson?.packagePrice || 0);
        return totalActualPrice - packagePrice;
    };

    const calculateDiscountPercentage = () => {
        const totalActualPrice = parseFloat(ApiReducer?.apiJson?.totalActualPrice || 0);
        const discountAmount = calculateDiscountAmount();
        if (totalActualPrice === 0) return 0;
        return (discountAmount / totalActualPrice) * 100;
    };

    const handleSubmit = () => {
        setSubmitLoading(true);

        // Calculate discount values before validation
        const discountAmount = calculateDiscountAmount();
        const discountPercentage = calculateDiscountPercentage();

        // Reconstruct validity object from separate fields
        const packageData = {
            ...ApiReducer.apiJson,
            discountAmount,
            discountPercentage,
            unitIds: ApiReducer.apiJson.unitIds || getSelectedUnit()?._id,
            validity: {
                value: ApiReducer.apiJson.validityValue || 30,
                unit: ApiReducer.apiJson.validityUnit || "days"
            }
        };

        // Remove the flattened fields before sending
        delete packageData.validityValue;
        delete packageData.validityUnit;

        PackageValidationSchema(packageData).then((errors) => {
            dispatch(setApiErrorJson(errors));

            if (Object.keys(errors).length === 0) {
                const endpoint = isEditMode ? updatePackage : addPackage;
                const successMessage = isEditMode ? 'Package updated successfully!' : 'Package created successfully!';
                const expectedStatusCode = isEditMode ? 200 : 201;

                HitApi(packageData, endpoint).then((res) => {
                    setSubmitLoading(false);
                    if (res?.statusCode === expectedStatusCode) {
                        toast.success(successMessage);
                        setTimeout(() => {
                            navigate('/packages');
                        }, 1000);
                    } else {
                        toast.error(res?.message || `Failed to ${isEditMode ? 'update' : 'create'} package. Please try again.`);
                    }
                }).catch((error) => {
                    setSubmitLoading(false);
                    console.error(`Error ${isEditMode ? 'updating' : 'creating'} package:`, error);
                    toast.error(`Error ${isEditMode ? 'updating' : 'creating'} package. Please try again.`);
                });
            } else {
                setSubmitLoading(false);
            }
        }).catch((validationError) => {
            setSubmitLoading(false);
            console.error('Validation error:', validationError);
        });
    };



    if (loading && isEditMode) {
        return (
            <div className='p-5'>
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading package data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className='p-5'>
            <PageHeader
                title={isEditMode ? 'Edit Package' : 'Add Package'}
                description={isEditMode ? 'Update existing package configuration' : 'Create and configure new service package'}
            />

            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Package Information</span>
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <AppInput
                        title="Package Name"
                        placeholder="e.g., Basic Grooming Package"
                        name="name"
                        important={true}
                        error={hasError('name', ApiReducer)}
                        errormsg={getErrorMsg('name', ApiReducer)}
                        icon={<Package size={18} />}
                        disabled={submitLoading}
                    />

                    <MultiSelect
                        title="Services"
                        name="services"
                        options={servicesOptions}
                        placeholder="Select services to include in this package"
                        important={true}
                        error={hasError('services', ApiReducer)}
                        errormsg={getErrorMsg('services', ApiReducer)}
                        disabled={submitLoading || isLoadingOptions}
                    />
                </div>

                <div className="mb-6">
                    <TextArea
                        title="Description"
                        placeholder="Package description"
                        name="description"
                        important={true}
                        error={hasError('description', ApiReducer)}
                        errormsg={getErrorMsg('description', ApiReducer)}
                        rows={4}
                        disabled={submitLoading}
                    />
                </div>

                {/* Pricing Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <AppInput
                        title="Total Actual Price (Auto-calculated)"
                        placeholder="Select services to calculate"
                        name="totalActualPrice"
                        type="number"
                        important={true}
                        error={hasError('totalActualPrice', ApiReducer)}
                        errormsg={getErrorMsg('totalActualPrice', ApiReducer)}
                        icon={<DollarSign size={18} />}
                        disabled={true}
                    />
                    <AppInput
                        title="Package Price"
                        placeholder="e.g., 900"
                        name="packagePrice"
                        type="number"
                        important={true}
                        error={hasError('packagePrice', ApiReducer)}
                        errormsg={getErrorMsg('packagePrice', ApiReducer)}
                        icon={<Tag size={18} />}
                        disabled={submitLoading}
                    />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Discount
                        </label>
                        <div className="px-3 py-2 bg-gray-50 border rounded-lg">
                            <div className="text-sm font-medium text-gray-900">
                                ₹{calculateDiscountAmount().toFixed(2)} ({calculateDiscountPercentage().toFixed(2)}%)
                            </div>
                        </div>
                    </div>
                </div>

                {/* Validity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <AppInput
                        title="Validity Value"
                        placeholder="e.g., 30"
                        name="validityValue"
                        type="number"
                        important={true}
                        error={hasError('validityValue', ApiReducer)}
                        errormsg={getErrorMsg('validityValue', ApiReducer)}
                        icon={<Calendar size={18} />}
                        disabled={submitLoading}
                    />
                    <SearchableSelect
                        title="Validity Unit"
                        name="validityUnit"
                        options={duration}
                        important={true}
                        error={hasError('validityUnit', ApiReducer)}
                        errormsg={getErrorMsg('validityUnit', ApiReducer)}
                        disabled={submitLoading}
                        placeholder="Select validity unit"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <AppSwitch
                        title="Package Status"
                        name="isActive"
                        specification="Toggle to enable or disable the package"
                    />
                </div>

                <div className='mt-5 flex justify-end'>
                    <AppButton
                        buttontext={submitLoading ? "Processing..." : (isEditMode ? "Update Package" : "Add Package")}
                        onClick={handleSubmit}
                        disabled={submitLoading}
                        isLoading={submitLoading}
                    />
                </div>
            </div>
        </div>
    );
}

export default AddPackage;